import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ColorLensOutlinedIcon from '@mui/icons-material/ColorLensOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { Team } from '../../models/training.models';
import { TeamDetails } from '../../models/team.models';
import { User } from '../../models/user.models';
import TeamsService from '../../services/teams.service';
import PaymentSubscriptionService from '../../services/payment-subscription.service';
import { CATALOG_PLAN_LABELS } from '../../helpers/billing.helpers';
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import COEInvoiceComponent from '../coe-invoice/coe-invoice.component';
import { TEAM_DETAILS_STORAGE_KEYS, clearStoredValue } from '../../helpers/cache.helpers';
import EditTeamDialog from './edit-team.dialog';
import ManageTeamMembersDialog from './manage-team-members.dialog';
import { palette } from '../../theme';

import './manage-team.dialog.css';

export interface ManageTeamDialogProps {
  open: boolean;
  onClose: () => void;
  team: Team;
  refreshTeam: () => void;
}

interface SubscriptionInfo {
  status?: string;
  startDate?: string;
  baseSubscriptionType?: string;
  baseSubscriptionPrice?: number | string;
  includedMembers?: number;
  additionalMembers?: number;
  pricePerAdditionalMember?: number | string;
  totalAmount?: number | string;
  latestInvoice?: string;
}

interface BillingInfo {
  brand?: string;
  last4?: string;
}

interface InvoiceListItem {
  id: string;
  month?: string;
}

const formatCurrency = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(2)}`;
};

const ManageTeamDialog: FC<ManageTeamDialogProps> = ({ open, onClose, team, refreshTeam }) => {
  const teamsService = useService(TeamsService);
  const paymentSubscriptionService = useService(PaymentSubscriptionService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();

  const [codeLoading, setCodeLoading] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [manageMembersDialogOpen, setManageMembersDialogOpen] = useState(false);
  const [cancelSubOpen, setCancelSubOpen] = useState(false);
  const [cancelSubLoading, setCancelSubLoading] = useState(false);
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [editTeamLoading, setEditTeamLoading] = useState(false);

  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionAbsent, setSubscriptionAbsent] = useState(false);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const isCoach = team.role?.toLowerCase() === 'coach';
  const hasInviteCode = Boolean(team.inviteCode);
  const teamActive = team.status === 'active' && !team.billingRequired;
  const teamId = (team.id || team.teamId) as string;

  const currentUserEmail = useMemo((): string | null => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      return (JSON.parse(raw) as User).email?.toLowerCase() ?? null;
    } catch {
      return null;
    }
  }, []);

  const isCreator =
    isCoach && !!currentUserEmail && !!team.creatorEmail && team.creatorEmail.toLowerCase() === currentUserEmail;

  const subtitle = team.description || '';

  const isUnlimitedPlan = team.plan === 'unlimited';
  const catalogPlanLabel = team.plan ? CATALOG_PLAN_LABELS[team.plan] : null;
  const isCatalogPlan = Boolean(catalogPlanLabel);

  const fetchSubscriptionData = useCallback(() => {
    if (!open || !isCreator || !teamId || isCatalogPlan) {
      setSubscriptionInfo(null);
      setHasSubscription(false);
      setSubscriptionAbsent(false);
      setBillingInfo(null);
      setInvoices([]);
      return;
    }

    setSubscriptionLoading(true);

    paymentSubscriptionService
      .getPaymentSubscriptionDetails(teamId)
      .then((resp) => {
        const data = resp.data?.data;
        if (data) {
          setSubscriptionInfo(data);
          setHasSubscription(true);
          setSubscriptionAbsent(false);

          // Only fetch billing/invoices once we know the team has an active
          // subscription — these endpoints 401 for teams without one, and a
          // stray 401 trips the global session-expired logout interceptor.
          setInvoicesLoading(true);

          paymentSubscriptionService
            .getBilling(teamId)
            .then((billingResp) => setBillingInfo(billingResp.data?.data || null))
            .catch(() => setBillingInfo(null));

          paymentSubscriptionService
            .getInvoices(teamId)
            .then((invoicesResp) => setInvoices(invoicesResp.data?.data || []))
            .catch(() => setInvoices([]))
            .finally(() => setInvoicesLoading(false));
        } else {
          setSubscriptionInfo(null);
          setHasSubscription(false);
          setSubscriptionAbsent(true);
          setBillingInfo(null);
          setInvoices([]);
        }
      })
      .catch(() => {
        setSubscriptionInfo(null);
        setHasSubscription(false);
        setSubscriptionAbsent(false);
        setBillingInfo(null);
        setInvoices([]);
      })
      .finally(() => setSubscriptionLoading(false));
  }, [open, isCreator, teamId, isCatalogPlan, paymentSubscriptionService]);

  useEffect(() => {
    if (open) {
      setSelectedInvoiceId(null);
      fetchSubscriptionData();
    }
  }, [open, fetchSubscriptionData]);

  const handleCopyCode = () => {
    if (!team.inviteCode) return;
    void navigator.clipboard.writeText(team.inviteCode).then(
      () => showSnackbar('Copied to clipboard.', 'success'),
      () => showSnackbar('Could not copy.', 'error')
    );
  };

  const handleToggleCode = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!isCoach || !teamActive || codeLoading) return;
    setCodeLoading(true);
    const action = checked ? teamsService.postTeamInviteCode(teamId) : teamsService.deleteTeamInviteCode(teamId);

    action
      .then(() => {
        showSnackbar(checked ? 'Invite code enabled.' : 'Invite code deactivated.', 'success');
        refreshTeam();
      })
      .catch(() => {
        showSnackbar(checked ? 'Could not generate invite code.' : 'Could not deactivate invite code.', 'error');
      })
      .finally(() => setCodeLoading(false));
  };

  const handleRequestNewCode = () => {
    if (!isCoach || !teamActive || codeLoading) return;
    setCodeLoading(true);
    teamsService
      .postTeamInviteCode(teamId)
      .then(() => {
        showSnackbar('New invite code generated.', 'success');
        refreshTeam();
      })
      .catch(() => showSnackbar('Could not generate invite code.', 'error'))
      .finally(() => setCodeLoading(false));
  };

  const handleLeaveTeam = () => {
    setLeaveLoading(true);
    teamsService
      .leaveTeam(teamId)
      .then(() => {
        clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
        showSnackbar('You have left the team', 'success');
        navigate('/teams');
      })
      .catch(() => {
        setLeaveLoading(false);
        showSnackbar('There was an error leaving the team, please try again', 'error');
      });
  };

  const handleDeleteTeam = () => {
    setDeleteLoading(true);
    teamsService
      .deleteTeam(teamId)
      .then((response) => {
        const outcome = (response.data?.data as { outcome?: string } | undefined)?.outcome;
        const message = response.data?.message;
        if (outcome === 'scheduled') {
          setDeleteLoading(false);
          setDeleteOpen(false);
          showSnackbar(
            message || 'This team stays active until the end of its billing period and will not renew.',
            'success'
          );
          onClose();
          return;
        }
        clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
        showSnackbar(
          message || 'Team successfully deactivated. Contact our support team if this was done in error',
          'success'
        );
        navigate('/teams');
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        setDeleteLoading(false);
        showSnackbar(
          error.response?.data?.message || 'There was an error deactivating the team, please try again',
          'error'
        );
      });
  };

  const handleOpenBilling = () => {
    onClose();
    navigate('/billing');
  };

  const handleOpenManageMembers = () => setManageMembersDialogOpen(true);

  const handleCancelSubscription = () => {
    setCancelSubLoading(true);
    paymentSubscriptionService
      .cancelSubscription(teamId)
      .then(() => {
        showSnackbar(
          'Successfully cancelled team subscription. You may view all team aspects but will no longer be able to use any actions',
          'success'
        );
        setCancelSubOpen(false);
        onClose();
        navigate('/teams');
      })
      .catch(() => {
        setCancelSubLoading(false);
        showSnackbar(
          'An error occured while cancelling your subscription. Please reach out to customer support',
          'error'
        );
      });
  };

  const handleEditTeamDetails = (teamDetails: TeamDetails) => {
    setEditTeamLoading(true);
    teamsService
      .editTeamDetails(teamId, teamDetails)
      .then(() => {
        setEditTeamOpen(false);
        setEditTeamLoading(false);
        refreshTeam();
        showSnackbar('Successfully updated your team details.', 'success');
      })
      .catch(() => {
        setEditTeamLoading(false);
        showSnackbar('There was an error updating your team', 'error');
      });
  };

  const handleMembersSaved = () => {
    refreshTeam();
    fetchSubscriptionData();
  };

  const parseMoney = (value: number | string | undefined): number => {
    if (value === undefined || value === null || value === '') return 0;
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    return Number.isNaN(num) ? 0 : num;
  };

  const detailRows: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    value: React.ReactNode;
  }> = [
    {
      key: 'name',
      label: 'Name',
      icon: <GroupsOutlinedIcon sx={{ fontSize: 13 }} />,
      iconBg: palette.purple.light,
      iconColor: palette.purple.main,
      value: team.name || '—',
    },
    {
      key: 'bio',
      label: 'Bio',
      icon: <SubjectOutlinedIcon sx={{ fontSize: 13 }} />,
      iconBg: palette.purple.light,
      iconColor: palette.purple.main,
      value: team.description || '—',
    },
    {
      key: 'contact',
      label: 'Contact',
      icon: <EmailOutlinedIcon sx={{ fontSize: 13 }} />,
      iconBg: palette.primary.light,
      iconColor: palette.primary.main,
      value: team.creatorEmail || '—',
    },
    {
      key: 'color',
      label: 'Color',
      icon: <ColorLensOutlinedIcon sx={{ fontSize: 13 }} />,
      iconBg: palette.warning.light,
      iconColor: palette.warning.main,
      value: (
        <span
          className="manage-team-color-swatch"
          style={{ backgroundColor: team.color || palette.secondary.main }}
          aria-label={team.color || 'team color'}
        />
      ),
    },
    {
      key: 'created',
      label: 'Created',
      icon: <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />,
      iconBg: '#FCE7F3',
      iconColor: '#DB2777',
      value: team.createdAt ? dayjs(team.createdAt).format('MMM D, YYYY') : '—',
    },
  ];

  const subscriptionCanceled = subscriptionInfo?.status === 'canceled';
  const showLegacyUnlimitedUi = isUnlimitedPlan || hasSubscription;
  const planDisplayName = isCatalogPlan
    ? catalogPlanLabel || 'Team Plan'
    : isUnlimitedPlan
    ? 'Unlimited (founding)'
    : subscriptionInfo?.baseSubscriptionType || 'Team Plan';

  const renderSubscriptionSection = () => {
    if (!isCreator) return null;

    return (
      <>
        <Typography className="manage-team-sec-head" component="div">
          <CreditCardOutlinedIcon sx={{ fontSize: 15 }} />
          Subscription &amp; Billing
        </Typography>

        {subscriptionLoading && !isCatalogPlan ? (
          <Box className="manage-team-loading">
            <CircularProgress size={28} />
          </Box>
        ) : isCatalogPlan ? (
          <Box className="manage-team-panel manage-team-basic-prompt">
            <Box className="manage-team-plan-badge">
              <Box className="manage-team-plan-icon">
                <StarOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Box>
                <Typography className="manage-team-plan-name">{planDisplayName}</Typography>
                <Typography className="manage-team-plan-hint">
                  This team is a line on your monthly or yearly bill. Other teams on that bill share the renewal date.
                </Typography>
              </Box>
            </Box>
            <Button className="manage-team-primary-btn" onClick={handleOpenBilling}>
              Go to Billing
            </Button>
          </Box>
        ) : isUnlimitedPlan && subscriptionAbsent ? (
          <Box className="manage-team-panel">
            <Typography className="manage-team-card-title">Plan Summary</Typography>
            <Typography className="manage-team-card-subtitle">
              Founding Unlimited. Members are included on this team, so there is nothing to purchase here.
            </Typography>
            <Box className="manage-team-plan-badge">
              <Box className="manage-team-plan-icon">
                <StarOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography className="manage-team-plan-name">{planDisplayName}</Typography>
                <Typography className="manage-team-plan-status">Active · Legacy</Typography>
              </Box>
            </Box>
          </Box>
        ) : !showLegacyUnlimitedUi ? (
          <Box className="manage-team-panel manage-team-basic-prompt">
            <Box className="manage-team-plan-badge">
              <Box className="manage-team-plan-icon">
                <StarOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Box>
                <Typography className="manage-team-plan-name">Basic Team</Typography>
                <Typography className="manage-team-plan-hint">Choose Small Team, Large Team, or Academic</Typography>
              </Box>
            </Box>
            <Button className="manage-team-primary-btn" onClick={handleOpenBilling}>
              Choose a plan
            </Button>
          </Box>
        ) : (
          <>
            <Box className="manage-team-dialog-columns">
              <Box className="manage-team-panel">
                <Typography className="manage-team-card-title">Plan Summary</Typography>
                <Typography className="manage-team-card-subtitle">
                  Founding Unlimited — legacy plan. Not available for new purchases. Cancel anytime; plan changes are
                  handled manually by CoeLab if needed.
                </Typography>

                <Box className="manage-team-plan-badge">
                  <Box className="manage-team-plan-icon">
                    <StarOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography className="manage-team-plan-name">{planDisplayName}</Typography>
                    <Typography className="manage-team-plan-status">
                      {subscriptionCanceled ? 'Canceled' : 'Active · Legacy'}
                    </Typography>
                  </Box>
                </Box>

                <Typography className="manage-team-panel-label">Items</Typography>
                <Box className="manage-team-billing-row">
                  <Typography className="manage-team-detail-label">{planDisplayName}</Typography>
                  <Typography className="manage-team-billing-value">
                    {formatCurrency(subscriptionInfo?.baseSubscriptionPrice)}/mo
                  </Typography>
                </Box>
                <Box className="manage-team-billing-row">
                  <Box>
                    <Typography className="manage-team-detail-label">
                      Additional Members ({subscriptionInfo?.additionalMembers ?? 0})
                    </Typography>
                    <Typography className="manage-team-card-subtitle" sx={{ mt: 0.25 }}>
                      * First {subscriptionInfo?.includedMembers ?? 0} are free
                    </Typography>
                  </Box>
                  <Typography className="manage-team-billing-value">
                    {formatCurrency(
                      Number(subscriptionInfo?.additionalMembers || 0) *
                        Number(subscriptionInfo?.pricePerAdditionalMember || 0)
                    )}
                    /mo
                  </Typography>
                </Box>

                <Box className="manage-team-amount-due">
                  <Typography className="manage-team-amount-label">Amount Due</Typography>
                  <Box className="manage-team-billing-row" sx={{ borderBottom: 'none', pt: 1 }}>
                    <Typography className="manage-team-detail-label">Total</Typography>
                    <Typography className="manage-team-total-value">
                      {formatCurrency(subscriptionInfo?.totalAmount)}/mo
                    </Typography>
                  </Box>
                </Box>

                {hasSubscription && !subscriptionCanceled ? (
                  <Box className="manage-team-account-actions">
                    <Button className="manage-team-text-link" onClick={handleOpenManageMembers}>
                      Manage Additional Athletes
                    </Button>
                    <Button
                      className="manage-team-text-link manage-team-text-link-danger"
                      onClick={() => setCancelSubOpen(true)}
                    >
                      Cancel Subscription
                    </Button>
                  </Box>
                ) : null}
              </Box>

              <Box className="manage-team-panel">
                <Typography className="manage-team-card-title">Payment Method</Typography>
                <Typography className="manage-team-card-subtitle">Manage your payment details</Typography>

                {billingInfo?.brand || billingInfo?.last4 ? (
                  <Box className="manage-team-payment-box">
                    <Box className="manage-team-payment-row">
                      <CreditCardOutlinedIcon sx={{ fontSize: 18, color: palette.primary.main }} />
                      <Typography className="manage-team-billing-value">
                        {(billingInfo.brand || 'Card').toUpperCase()}
                        {billingInfo.last4 ? ` •••• ${billingInfo.last4}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography className="manage-team-card-subtitle" sx={{ mb: 2 }}>
                    No payment method on file
                  </Typography>
                )}

                <Tooltip title="Contact support to update your payment method">
                  <span>
                    <Button className="manage-team-primary-btn" disabled fullWidth>
                      Update Payment
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Box className="manage-team-divider" />

            <Typography className="manage-team-section-label" component="div">
              <ReceiptOutlinedIcon sx={{ fontSize: 12 }} />
              Billing History
            </Typography>

            <Box className="manage-team-invoice-table">
              <Box className="manage-team-invoice-header">
                <Typography className="manage-team-invoice-col-label">Invoice</Typography>
                <Typography className="manage-team-invoice-col-label">Period</Typography>
              </Box>
              {invoicesLoading ? (
                <Box className="manage-team-loading">
                  <CircularProgress size={22} />
                </Box>
              ) : invoices.length === 0 ? (
                <Typography className="manage-team-card-subtitle" sx={{ p: 2, textAlign: 'center' }}>
                  No invoices yet
                </Typography>
              ) : (
                invoices.map((invoice) => (
                  <Box
                    key={invoice.id}
                    className={`manage-team-invoice-row${
                      selectedInvoiceId === invoice.id ? ' manage-team-invoice-row-active' : ''
                    }`}
                    onClick={() => setSelectedInvoiceId(selectedInvoiceId === invoice.id ? null : invoice.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setSelectedInvoiceId(selectedInvoiceId === invoice.id ? null : invoice.id);
                      }
                    }}
                  >
                    <Box className="manage-team-invoice-id">
                      <ReceiptOutlinedIcon sx={{ fontSize: 14, color: palette.primary.main }} />
                      <Typography className="manage-team-billing-value">{invoice.id}</Typography>
                    </Box>
                    <Typography className="manage-team-detail-label">{invoice.month || '—'}</Typography>
                  </Box>
                ))
              )}
            </Box>

            {selectedInvoiceId ? (
              <Box className="manage-team-invoice-detail">
                <COEInvoiceComponent key={selectedInvoiceId} teamId={teamId} invoiceId={selectedInvoiceId} />
              </Box>
            ) : null}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: 'manage-team-dialog-paper' }}>
        <Box className="manage-team-dialog-header">
          <IconButton className="manage-team-dialog-close" onClick={onClose} aria-label="Close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box className="manage-team-dialog-header-identity">
            <Box className="manage-team-dialog-avatar" style={{ backgroundColor: team.color || palette.warning.main }}>
              {(team.name || 'T').charAt(0).toUpperCase()}
            </Box>
            <Box>
              <Typography className="manage-team-dialog-title">{team.name}</Typography>
              {subtitle ? <Typography className="manage-team-dialog-subtitle">{subtitle}</Typography> : null}
            </Box>
          </Box>
        </Box>

        <Box className="manage-team-dialog-body">
          <Box className="manage-team-dialog-columns">
            <Box className="manage-team-panel">
              <Box className="manage-team-panel-label-row">
                <Typography className="manage-team-panel-label">Team Details</Typography>
                {isCoach && teamActive ? (
                  <IconButton
                    size="small"
                    aria-label="Edit team"
                    onClick={() => setEditTeamOpen(true)}
                    sx={{ color: palette.primary.main }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null}
              </Box>
              {detailRows.map((row) => (
                <Box key={row.key} className="manage-team-detail-row">
                  <Box className="manage-team-detail-left">
                    <Box
                      className="manage-team-detail-icon"
                      style={{ backgroundColor: row.iconBg, color: row.iconColor }}
                    >
                      {row.icon}
                    </Box>
                    <Typography className="manage-team-detail-label">{row.label}</Typography>
                  </Box>
                  <Typography className="manage-team-detail-value" component="div">
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box className="manage-team-panel manage-team-panel-code">
              <Typography className="manage-team-panel-label">Team Code</Typography>
              <Box className="manage-team-code-box">
                <Typography className="manage-team-code-hint">Share to invite members</Typography>
                <Typography className="manage-team-code-value">
                  {team.inviteCode || (isCoach ? 'No code yet' : '—')}
                </Typography>
              </Box>
              <Button
                className="manage-team-copy-btn"
                onClick={handleCopyCode}
                disabled={!hasInviteCode || !teamActive}
                fullWidth
              >
                Copy Team Code
              </Button>
              {isCoach ? (
                <>
                  <Box className="manage-team-code-row">
                    <Box className="manage-team-detail-left">
                      <Box
                        className="manage-team-detail-icon"
                        style={{ backgroundColor: palette.success.light, color: palette.success.main }}
                      >
                        <CheckOutlinedIcon sx={{ fontSize: 13 }} />
                      </Box>
                      <Typography className="manage-team-detail-label">
                        {hasInviteCode ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Box>
                    {codeLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Switch
                        checked={hasInviteCode}
                        onChange={handleToggleCode}
                        disabled={!teamActive}
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>
                  <Box
                    className={`manage-team-code-row manage-team-code-row-click${
                      !teamActive || codeLoading ? ' manage-team-code-row-disabled' : ''
                    }`}
                    onClick={handleRequestNewCode}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRequestNewCode();
                    }}
                  >
                    <Box className="manage-team-detail-left">
                      <Box
                        className="manage-team-detail-icon"
                        style={{ backgroundColor: palette.primary.light, color: palette.primary.main }}
                      >
                        <RefreshOutlinedIcon sx={{ fontSize: 13 }} />
                      </Box>
                      <Typography className="manage-team-detail-label">Request New Code</Typography>
                    </Box>
                  </Box>
                </>
              ) : null}
            </Box>
          </Box>

          {renderSubscriptionSection()}

          <Typography className="manage-team-sec-head" component="div">
            Danger Zone
          </Typography>

          <Box className="manage-team-footer">
            <Button
              className="manage-team-leave-btn"
              startIcon={<LogoutOutlinedIcon />}
              onClick={() => setLeaveOpen(true)}
              disabled={!teamActive || isCreator}
            >
              Leave Team
            </Button>
            {isCreator ? (
              <Button
                className="manage-team-delete-btn"
                startIcon={<DeleteOutlineOutlinedIcon />}
                onClick={() => setDeleteOpen(true)}
                disabled={!teamActive}
              >
                Delete Team
              </Button>
            ) : null}
          </Box>
        </Box>
      </Dialog>

      <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)}>
        <DialogTitle>Leave team?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isCreator
              ? 'As the team creator you cannot leave. Delete the team instead if you want to shut it down.'
              : 'You will lose access to all team content. This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveOpen(false)}>Cancel</Button>
          {isCreator ? null : (
            <Button color="error" variant="contained" disabled={leaveLoading} onClick={handleLeaveTeam}>
              {leaveLoading ? <CircularProgress size={18} color="inherit" /> : 'Leave Team'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete team?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isCatalogPlan
              ? 'A paid team stays available until the renewal date and then drops off the bill. There is no charge today. Other teams on that bill keep renewing. A free trial ends immediately.'
              : 'This will deactivate the team and members will lose access. Records will still be preserved.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={deleteLoading} onClick={handleDeleteTeam}>
            {deleteLoading ? <CircularProgress size={18} color="inherit" /> : 'Delete Team'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelSubOpen} onClose={() => !cancelSubLoading && setCancelSubOpen(false)}>
        <DialogTitle>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Canceling your subscription will stop all Team feature use once the month concludes PERMANENTLY. You will
            still have access to all Team information remaining from the point you cancel your subscription however you
            cannot renew this team. To gain team access you must create a new team.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelSubOpen(false)} disabled={cancelSubLoading}>
            Keep Subscription
          </Button>
          <Button color="error" variant="contained" disabled={cancelSubLoading} onClick={handleCancelSubscription}>
            {cancelSubLoading ? <CircularProgress size={18} color="inherit" /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      <EditTeamDialog
        isOpen={editTeamOpen}
        isLoading={editTeamLoading}
        teamName={team.name || ''}
        teamColor={team.color || ''}
        teamDescription={(team.description as string) || ''}
        handleClose={() => !editTeamLoading && setEditTeamOpen(false)}
        handleSubmit={handleEditTeamDetails}
      />

      <ManageTeamMembersDialog
        open={manageMembersDialogOpen}
        onClose={() => setManageMembersDialogOpen(false)}
        teamId={teamId}
        includedMembers={subscriptionInfo?.includedMembers ?? 0}
        additionalMembers={subscriptionInfo?.additionalMembers ?? 0}
        pricePerAdditionalMember={parseMoney(subscriptionInfo?.pricePerAdditionalMember)}
        basePrice={parseMoney(subscriptionInfo?.baseSubscriptionPrice)}
        onSaved={handleMembersSaved}
      />
    </>
  );
};

export default ManageTeamDialog;
